import { useEffect, useRef } from 'react';

/**
 * Cmd/Ctrl+Z undoes and Shift+Cmd/Ctrl+Z (or Ctrl+Y) redoes, for an editor
 * that keeps a history (KPR-348, hooks/useUndoHistory).
 *
 * Three things keep it from taking keys that are not its own:
 * - A TEXT field keeps its own undo. Typing in a name, a number or a template
 *   is stepped back by the browser, character by character, the way a person
 *   expects there; only focus on something that is not text entry (a handle, a
 *   switch, a radio, the page) reaches the editor's history.
 * - `enabled` is false while a gesture is held (a drag, a picked-up field, a
 *   save on its way) or when the editor is not on screen. With nothing to step
 *   the key is still the editor's, and says so ("Nothing to undo.").
 * - `accepts(target)` lets the caller refuse a target: a dialog or a dropdown
 *   open over the editor is not the editor.
 */

const NOT_TEXT = new Set(['checkbox', 'radio', 'button', 'submit', 'reset', 'range', 'color', 'file', 'image']);

/** Whether the keys are the browser's own: a field the person types into. */
export function isTextEntry(target) {
	if (!target || typeof target !== 'object') return false;
	if (target.isContentEditable) return true;
	// jsdom has no isContentEditable; a rich text box is also its attribute.
	const editable = target.closest ? target.closest('[contenteditable]') : null;
	if (editable && editable.getAttribute('contenteditable') !== 'false') return true;
	const tag = target.tagName;
	if (tag === 'TEXTAREA' || tag === 'SELECT') return true;
	// antd's Select keeps focus on a READ-ONLY input (role combobox): nothing to
	// type into, so nothing for the browser to undo, and the editor's undo is
	// what the person means. A searchable Select's input is editable, and is text.
	if (tag === 'INPUT') return !target.readOnly && !NOT_TEXT.has(String(target.type || 'text').toLowerCase());
	return false;
}

export default function useUndoShortcuts({ enabled = true, onUndo, onRedo, accepts }) {
	const latest = useRef({ onUndo, onRedo, accepts });
	latest.current = { onUndo, onRedo, accepts };

	useEffect(() => {
		if (!enabled) return undefined;
		const onKeyDown = (event) => {
			if (event.defaultPrevented || event.isComposing || event.altKey) return;
			const key = String(event.key || '').toLowerCase();
			const mod = event.metaKey || event.ctrlKey;
			if (!mod) return;
			let action = null;
			if (key === 'z') action = event.shiftKey ? 'redo' : 'undo';
			// Windows and Linux's second spelling of redo. Cmd+Y is a browser's own (History).
			else if (key === 'y' && event.ctrlKey && !event.metaKey && !event.shiftKey) action = 'redo';
			if (!action) return;
			if (isTextEntry(event.target)) return;
			const { accepts: accept, onUndo: undo, onRedo: redo } = latest.current;
			if (accept && !accept(event.target)) return;
			event.preventDefault();
			if (action === 'undo') undo?.();
			else redo?.();
		};
		document.addEventListener('keydown', onKeyDown);
		return () => document.removeEventListener('keydown', onKeyDown);
	}, [enabled]);
}
